// "Continue with Apple" for the signup / login redesign: Sign in with Apple JS in popup mode with the
// identity scope (name email). The one-time authorization code, the name Apple shares on the first
// sign-in and the nonce go to POST /api/auth/social/exchange/, where the backend exchanges the code
// with the Services ID's client secret and verifies the identity. Apple needs https and a registered
// domain, so the dev build leaves APPLE_SIGNIN_CLIENT_ID empty and the button stays hidden there.
const APPLE_SRC = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'

const CLIENT_ID = typeof APPLE_SIGNIN_CLIENT_ID !== 'undefined' ? APPLE_SIGNIN_CLIENT_ID : ''
const REDIRECT_URI = typeof APPLE_SIGNIN_REDIRECT_URI !== 'undefined' ? APPLE_SIGNIN_REDIRECT_URI : ''

let scriptPromise = null

export function isAppleSignInConfigured () {
  return Boolean(CLIENT_ID && REDIRECT_URI)
}

function appleReady () {
  return Boolean(window.AppleID && window.AppleID.auth)
}

export function loadAppleIdentity () {
  if (appleReady()) return Promise.resolve()
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = APPLE_SRC
      script.async = true
      script.onload = resolve
      script.onerror = () => {
        scriptPromise = null
        reject(new Error('Failed to load the Apple sign-in script'))
      }
      document.head.appendChild(script)
    })
  }
  return scriptPromise
}

function randomToken () {
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Open the Apple popup. Resolves `{ code, name, nonce }`; rejects with `popupClosed: true` on the
 * error when the user dismissed it. `name` is empty after the first sign-in (Apple's rule).
 */
export async function requestAppleIdentity () {
  if (!isAppleSignInConfigured()) throw new Error('Sign in with Apple is not configured')
  await loadAppleIdentity()
  const state = randomToken()
  const nonce = randomToken()
  window.AppleID.auth.init({
    clientId: CLIENT_ID, scope: 'name email', redirectURI: REDIRECT_URI, state, nonce, usePopup: true,
  })
  let result
  try {
    result = await window.AppleID.auth.signIn()
  } catch (e) {
    const code = (e && e.error) || ''
    const error = new Error(code || 'Apple sign-in failed')
    error.popupClosed = code === 'popup_closed_by_user' || code === 'user_cancelled_authorize'
    throw error
  }
  const authorization = (result && result.authorization) || {}
  if (!authorization.code) throw new Error('Apple returned no authorization code')
  if (authorization.state && authorization.state !== state) throw new Error('Apple state mismatch')
  const user = (result && result.user) || {}
  const parts = user.name ? [user.name.firstName, user.name.lastName] : []
  return { code: authorization.code, name: parts.filter(Boolean).join(' ').trim(), nonce }
}
